/**
 * CompletenessBanner — run-manifest completeness verification banner.
 *
 * Surfaces, for the currently loaded Scan_Run:
 *  - the manifest `org` and `run_date` (Req 2.2),
 *  - the loaded vs. expected (`repo_count`) counts and the resulting
 *    completeness status — complete / incomplete / discrepancy /
 *    manifest-unavailable (Req 2.3–2.6),
 *  - the status-specific delta (missing for incomplete, extra for discrepancy),
 *  - the count of repos present in the run but that could not be parsed/loaded
 *    (`unparseableRepoNames`), surfaced here per Req 10.5.
 *
 * Classification is delegated entirely to the pure `compareCompleteness`
 * helper so the banner is a thin presentation layer over tested logic.
 */
import {
  compareCompleteness,
  type CompletenessStatus,
} from "../lib/completeness";

/** Props for {@link CompletenessBanner}. */
export interface CompletenessBannerProps {
  /** Number of Repo_Results that loaded successfully (`L`). */
  loaded: number;
  /**
   * Manifest `repo_count` (`E`), or `null` when the manifest is unavailable
   * (Req 2.6).
   */
  expectedRepoCount: number | null;
  /** Manifest `org` (Req 2.2). */
  org: string;
  /** Manifest `run_date`, `YYYY-MM-DD` (Req 2.2). */
  runDate: string;
  /**
   * Count of repos present in the run but failing schema validation — i.e.
   * could-not-load/unparseable results (Req 10.5). Defaults to `0`.
   */
  unparseableCount?: number;
}

/** Human-readable label per completeness status. */
const STATUS_LABEL: Record<CompletenessStatus, string> = {
  complete: "Complete",
  incomplete: "Incomplete",
  discrepancy: "Discrepancy",
  "manifest-unavailable": "Manifest unavailable",
};

/**
 * Completeness verification banner for a loaded Scan_Run.
 *
 * Renders the manifest identity (`org`, `run_date`), the loaded/expected
 * counts, the completeness status with a status-specific delta, and the
 * unparseable-repo count. The component is purely presentational: all
 * classification comes from {@link compareCompleteness}.
 *
 * Styling is token-driven via classes in `components.css`; the per-status
 * visual accent is selected from the `data-status` attribute, so the markup,
 * `role="status"`, `aria-live`, and `data-status` behavior are unchanged.
 */
export function CompletenessBanner({
  loaded,
  expectedRepoCount,
  org,
  runDate,
  unparseableCount = 0,
}: CompletenessBannerProps) {
  const result = compareCompleteness(loaded, expectedRepoCount);

  return (
    <section
      role="status"
      aria-live="polite"
      data-status={result.status}
      className="completeness-banner"
    >
      <span className="completeness-banner__badge">
        {STATUS_LABEL[result.status]}
      </span>

      {/* Manifest identity (Req 2.2) */}
      <span>
        <strong>{org}</strong> · run {runDate}
      </span>

      {/* Loaded vs. expected counts (Req 2.3–2.6) */}
      <span>
        Loaded <strong>{result.loaded}</strong> of{" "}
        <strong>
          {result.expected === null ? "unknown" : result.expected}
        </strong>{" "}
        expected
      </span>

      {/* Status-specific delta */}
      {result.status === "incomplete" && (
        <span>
          {result.missing} missing
        </span>
      )}
      {result.status === "discrepancy" && (
        <span>
          {result.extra} extra
        </span>
      )}
      {result.status === "manifest-unavailable" && (
        <span>Expected count could not be determined.</span>
      )}

      {/* Unparseable / could-not-load repos (Req 10.5) */}
      {unparseableCount > 0 && (
        <span>
          {unparseableCount}{" "}
          {unparseableCount === 1 ? "repo" : "repos"} could not be loaded
        </span>
      )}
    </section>
  );
}

export default CompletenessBanner;
